import * as sharePointDocumentOpener from "client_legacy/Documents/SharePoint/documentOpener";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	DocumentItemViewModel,
	DocumentItemType,
	FileViewModel,
	FolderViewModel,
	OneNoteNotebookViewModel
} from "client/Api/Documents/SharePoint/common";
import * as webDavWopiOpener from "client_legacy/Documents/WebDav/wopiOpener";
import * as sharePointOpenInLocalAppAction from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import * as webDavLocalAppOpener from "client_legacy/Documents/WebDav/localAppOpener";
import {
	downloadDocumentInline as sharePointDownloadDocumentInline
} from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import {
	downloadInline as webDavDownloadInline,
	openImagePreview as webDavOpenImagePreview,
	getWebDavFileRelativeUrl
} from "client/Features/Documents/WebDav/actions";
import {
	BookmarkModel,
	getSharePointModels,
	getWebDavModels,
	SharePointModels
} from "client/Api/Documents/Bookmarks/bookmarks";
import { previewImageInDialogAction } from "client_legacy/Documents/SharePoint/Actions/previewImageInDialogAction";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import {
	OpenLocalizations,
	getOpenLocalizationsForExtension
} from "client_legacy/Documents/openLocalizations";
import { ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import {
	WebDavActivationContext,
	buildWebDavActivationContext
} from "client/Features/Documents/WebDav/WebDavStore";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import {
	buildSharePointActivationContext,
	SharePointActivationContext
} from "client/Features/Documents/SharePoint/SharePointStore";
import { AlertManager } from "client/Hooks";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { WebDavModels } from "client/Api/Documents/WebDav/webDav";
import { BookmarkCouldNotBeOpenedAlertContent } from "client/Features/Documents/Bookmarks/BookmarkCouldNotBeOpenedAlertContent";
import { getSharePointLocationUrl, getWebDavFolderUrl } from "client/Features/Documents/routing";
import { getDownloadFileUrlPath } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { openUrlFile as openUrlFileHandler } from "client/Features/Documents/openUrlFile";

interface OpenerOptions {
	onOpenFolderRequestStart?: () => void;
	onOpenFolderRequestEnd?: () => void;
	onOpenFileRequestStart?: () => void;
	onOpenFileRequestEnd?: () => void;
	onChangeNavigationState?: (state: IStoreHistoryState) => void;
}

const NOOP = () => { /* nothing to do */ };

class Opener {

	private readonly _options: Required<OpenerOptions>;
	private readonly _alertManager: AlertManager;

	constructor(options: OpenerOptions, alertManager: AlertManager) {
		const {
			onOpenFolderRequestStart = NOOP,
			onOpenFolderRequestEnd = NOOP,
			onOpenFileRequestStart = NOOP,
			onOpenFileRequestEnd = NOOP,
			onChangeNavigationState = () => {
				// The error message is not logged by the handler, so we log it manually
				console.error("onChangeNavigationState was not provided so DMS page related actions cannot be executed");
				throw new Error();
			}
		} = options;

		this._options = {
			onOpenFolderRequestStart,
			onOpenFolderRequestEnd,
			onOpenFileRequestStart,
			onOpenFileRequestEnd,
			onChangeNavigationState
		};

		this._alertManager = alertManager;
	}

	public openFolderFromDocumentsPage(bookmark: BookmarkModel): void {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				this.retrieveSharePointModelsForOpenFolder(
					bookmark,
					({ view, item }) => {
						const activationContext: SharePointActivationContext = buildSharePointActivationContext(view, item as FolderViewModel);

						this._options.onChangeNavigationState({
							store: DocumentStoreType.SharePoint,
							...activationContext
						});
					}
				);
				break;
			case DocumentStoreType.WebDav:
				this.retrieveWebDavModelsForOpenFolder(
					bookmark,
					({ server, item }) => {
						const activationContext: WebDavActivationContext = buildWebDavActivationContext(server, item);

						this._options.onChangeNavigationState({
							store: DocumentStoreType.WebDav,
							...activationContext
						});
					}
				);
				break;
			default:
				never(bookmark);
		}
	}

	public openFolder(bookmark: BookmarkModel, openInNewWindow: boolean = false): Promise<void> {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFolder(
					bookmark,
					({ item }) => {
						const url: string = getSharePointLocationUrl(item.location, item.id);
						navigateTo(url, openInNewWindow);
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFolder(
					bookmark,
					({ server, item }) => {
						const url: string = getWebDavFolderUrl(server.id, item.id);
						navigateTo(url, openInNewWindow);
					}
				);
			default:
				never(bookmark);
		}
	}

	public openLocation(bookmark: BookmarkModel, openInNewWindow: boolean = false): Promise<void> {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFolder(
					bookmark,
					({ item }) => {
						const url: string = getSharePointLocationUrl(item.location, item.parentFolderId);
						navigateTo(url, openInNewWindow);
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFolder(
					bookmark,
					({ server, item }) => {
						const url: string = getWebDavFolderUrl(server.id, item.parentId);
						navigateTo(url, openInNewWindow);
					}
				);
			default:
				never(bookmark);
		}
	}

	public openFileInWopi(bookmark: BookmarkModel): Promise<void> {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmark,
					({ item }) => {
						sharePointDocumentOpener.openFileInWopi(item as FileViewModel | OneNoteNotebookViewModel);
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFile(
					bookmark,
					({ server, item }) => {
						webDavWopiOpener.open(server.id, item.id, item.name);
					}
				);
			default:
				never(bookmark);
		}
	}

	public openFileInSharePointViewer(bookmark: BookmarkModel): Promise<void> {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmark,
					({ item }) => {
						if (item.itemType !== DocumentItemType.File) {
							throw new Error("The document is not a file.");
						}

						sharePointDocumentOpener
							.openFileInSharePointViewer(item);
					}
				);
			case DocumentStoreType.WebDav:
				throw new Error("The share point viewer only supports share point files.");
			default:
				never(bookmark);
		}
	}

	public openFileLocal(bookmark: BookmarkModel, isTheOnlineEditorAvailable: boolean): Promise<void> {
		const extension: string = bookmark.itemType === DocumentItemType.OneNoteNotebook
			? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
			: bookmark.extension;
		const openLocalizations: OpenLocalizations = getOpenLocalizationsForExtension(extension);

		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmark,
					({ item }) => {
						sharePointOpenInLocalAppAction.open(item);

						if (isTheOnlineEditorAvailable) {
							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								openLocalizations,
								() => sharePointDocumentOpener.openFileInWopi(item as FileViewModel | OneNoteNotebookViewModel)
							);
						}
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFile(
					bookmark,
					({ server, item }) => {
						webDavLocalAppOpener.open(server, item);

						if (isTheOnlineEditorAvailable) {
							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								openLocalizations,
								() => webDavWopiOpener.open(server.id, item.id, item.name)
							);
						}
					}
				);
			default:
				never(bookmark);
		}
	}

	public openFileInline(bookmark: BookmarkModel): Promise<void> {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmark,
					({ item }) => {
						if (item.itemType === DocumentItemType.File) {
							sharePointDownloadDocumentInline(item);
						} else {
							throw new Error("Inline file opener doesn't support folders or OneNote files.");
						}
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFile(
					bookmark,
					({ server, item }) => {
						webDavDownloadInline(server.id, item.id);
					}
				);
			default:
				never(bookmark);
		}
	}

	public openInImagePreviewDialog(bookmarkedImage: BookmarkModel): Promise<void> {
		switch (bookmarkedImage.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmarkedImage,
					({ item }) => {
						previewImageInDialogAction.execute(item, [item]);
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFile(
					bookmarkedImage,
					({ server, item }) => {
						webDavOpenImagePreview(server.id, item, [item]);
					}
				);
			default:
				never(bookmarkedImage);
		}
	}

	public openUrlFile(bookmarkedUrlFile: BookmarkModel): Promise<void> {
		switch (bookmarkedUrlFile.storeType) {
			case DocumentStoreType.SharePoint:
				return this.retrieveSharePointModelsForOpenFile(
					bookmarkedUrlFile,
					({ item }) => {
						if (item.itemType !== DocumentItemType.File) {
							throw new Error("The document is not a file.");
						}

						const url: string = getDownloadFileUrlPath(item.id, item.location);
						openUrlFileHandler(url, this._alertManager);
					}
				);
			case DocumentStoreType.WebDav:
				return this.retrieveWebDavModelsForOpenFile(
					bookmarkedUrlFile,
					async ({ server, item }) => {
						const url: string = getWebDavFileRelativeUrl(item.id, server.id);
						await openUrlFileHandler(url, this._alertManager);
					}
				);
			default:
				never(bookmarkedUrlFile);
		}
	}

	private showCannotOpenBookmarkAlertBar(bookmark: BookmarkModel) {
		this._alertManager
			.showError(
				<BookmarkCouldNotBeOpenedAlertContent
					bookmark={bookmark}
					alertManager={this._alertManager}
				/>
			);
	}

	private showFileInfectedAlertBar() {
		this._alertManager
			.showWarning(localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile"));
	}

	private async retrieveSharePointModelsForOpenFolder(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: SharePointModels) => void,
	): Promise<void> {
		this._options.onOpenFolderRequestStart();
		await this.retrieveSharePointModels(
			bookmark,
			result => onSuccessCallback(result),
			() => this._options.onOpenFolderRequestEnd()
		);
	}

	private async retrieveSharePointModelsForOpenFile(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: SharePointModels) => void,
	): Promise<void> {
		this._options.onOpenFileRequestStart();
		await this.retrieveSharePointModels(
			bookmark,
			result => {
				const document: DocumentItemViewModel = result.item;

				if (document.itemType === DocumentItemType.File && document.isInfected) {
					this.showFileInfectedAlertBar();
					return;
				}

				onSuccessCallback(result);
			},
			() => this._options.onOpenFileRequestEnd()
		);
	}

	private async retrieveSharePointModels(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: SharePointModels) => void,
		finallyCallback: () => void,
	): Promise<void> {
		try {
			const result: SharePointModels = await getSharePointModels(bookmark.id);
			onSuccessCallback(result);
		} catch {
			this.showCannotOpenBookmarkAlertBar(bookmark);
		} finally {
			finallyCallback();
		}
	}

	private async retrieveWebDavModelsForOpenFolder(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: WebDavModels) => void,
	): Promise<void> {
		this._options.onOpenFolderRequestStart();
		await this.retrieveWebDavModels(
			bookmark,
			result => onSuccessCallback(result),
			() => this._options.onOpenFolderRequestEnd()
		);
	}

	private async retrieveWebDavModelsForOpenFile(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: WebDavModels) => void,
	): Promise<void> {
		this._options.onOpenFileRequestStart();
		await this.retrieveWebDavModels(
			bookmark,
			result => onSuccessCallback(result),
			() => this._options.onOpenFileRequestEnd()
		);
	}

	private async retrieveWebDavModels(
		bookmark: BookmarkModel,
		onSuccessCallback: (result: WebDavModels) => void,
		finallyCallback: () => void,
	): Promise<void> {
		try {
			const result: WebDavModels = await getWebDavModels(bookmark.id);
			onSuccessCallback(result);
		} catch {
			this.showCannotOpenBookmarkAlertBar(bookmark);
		} finally {
			finallyCallback();
		}
	}
}

export {
	Opener,
	type OpenerOptions
};
